import { useEffect, useMemo, useState } from "react";
import {
  TableWrapper,
  TableBody,
  TableRow,
  TableRowItem,
  TableFooter,
  TablePagination,
} from "@/components/ui/table";

import { AdminPayout, PayoutStatus } from "@/types";

const SORTABLE_FIELDS = [
  "company",
  "user",
  "jobOpening",
  "status",
  "lastAttemptAt",
  "createdAt",
] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

const STATUS_TEXT_MAP: Record<PayoutStatus, string> = {
  pending: "Pending",
  succeeded: "Succeeded",
  failed: "Failed",
};

const ENTRIES_PER_PAGE = 10;

const PayoutTable = ({ payouts }: { payouts: AdminPayout[] }) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortableField>("createdAt");
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  const sortedEntries: AdminPayout[] | undefined = useMemo(() => {
    switch (sortField) {
      case "company":
      case "user":
        return [...payouts].sort(
          (a, b) =>
            (a[sortField].name ?? "").localeCompare(b[sortField].name ?? "") *
            sortDirection,
        );
      case "jobOpening":
        return [...payouts].sort(
          (a, b) =>
            (a[sortField].role ?? "").localeCompare(b[sortField].role ?? "") *
            sortDirection,
        );
      case "status":
      case "createdAt":
        return [...payouts].sort(
          (a, b) => a[sortField].localeCompare(b[sortField]) * sortDirection,
        );
      case "lastAttemptAt":
        return [...payouts].sort(
          (a, b) =>
            (a[sortField] ?? "").localeCompare(b[sortField] ?? "") *
            sortDirection,
        );
    }
  }, [payouts, sortField, sortDirection]);

  const numberOfPages = useMemo(() => {
    if (!sortedEntries) {
      return 1;
    }

    return Math.ceil(sortedEntries.length / ENTRIES_PER_PAGE);
  }, [sortedEntries]);

  const currentPageEntries = useMemo(
    () =>
      sortedEntries?.slice(
        (page - 1) * ENTRIES_PER_PAGE,
        page * ENTRIES_PER_PAGE,
      ),
    [sortedEntries, page],
  );

  const onHeadingClick = (field: SortableField) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection(1);
    } else {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    }
  };

  useEffect(() => {
    if (page > numberOfPages) {
      setPage(numberOfPages || 1);
    }
  }, [page, numberOfPages]);

  return (
    <div className="py-6 self-stretch flex flex-col lg:flex-row gap-x-6 gap-y-2">
      <div className="flex-1 min-w-0">
        <TableWrapper>
          <TableBody>
            <thead>
              <TableRow>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("company")}
                  sortDirection={
                    sortField === "company" ? sortDirection : undefined
                  }
                >
                  Company
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("jobOpening")}
                  sortDirection={
                    sortField === "jobOpening" ? sortDirection : undefined
                  }
                >
                  Job Opening
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("user")}
                  sortDirection={
                    sortField === "user" ? sortDirection : undefined
                  }
                >
                  Candidate
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("status")}
                  sortDirection={
                    sortField === "status" ? sortDirection : undefined
                  }
                >
                  Status
                </TableRowItem>
                <TableRowItem heading>Total Amount</TableRowItem>
                <TableRowItem heading>Payout Attempts</TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("lastAttemptAt")}
                  sortDirection={
                    sortField === "lastAttemptAt" ? sortDirection : undefined
                  }
                >
                  Last Attempt At
                </TableRowItem>
                <TableRowItem
                  heading
                  onClick={() => onHeadingClick("createdAt")}
                  sortDirection={
                    sortField === "createdAt" ? sortDirection : undefined
                  }
                >
                  Scheduled At
                </TableRowItem>
              </TableRow>
            </thead>
            <tbody>
              {currentPageEntries?.map((payout) => (
                <AdminRow key={`payout-${payout.id}`} payout={payout} />
              ))}
            </tbody>
          </TableBody>
          {numberOfPages > 1 ? (
            <TableFooter>
              <TablePagination
                currentPage={page}
                totalPages={numberOfPages}
                navigateFn={setPage}
              />
            </TableFooter>
          ) : null}
        </TableWrapper>
      </div>
    </div>
  );
};

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const AdminRow = ({ payout }: { payout: AdminPayout }) => {
  return (
    <TableRow>
      <TableRowItem>
        <a
          href={`/new-admin/company/${payout.company.id}`}
          className="text-xs text-[#646cff] font-medium hover:underline"
        >
          {payout.company?.name ?? "Unnamed"}
        </a>
      </TableRowItem>
      <TableRowItem>
        <a
          href={`/new-admin/job/${payout.jobOpening.id}`}
          className="text-xs text-[#646cff] font-medium hover:underline"
        >
          {payout.jobOpening?.role ?? "Unnamed"}
        </a>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {payout.user?.name ?? "Unnamed"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {STATUS_TEXT_MAP[payout.status]}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {formatter.format(payout.totalAmountCents / 100)}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {payout.payoutAttempts.length}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {payout.lastAttemptAt
            ? new Date(payout.lastAttemptAt).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "Unknown"}
        </span>
      </TableRowItem>
      <TableRowItem>
        <span className="text-[#5C6060] text-sm">
          {new Date(payout.createdAt).toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          })}
        </span>
      </TableRowItem>
    </TableRow>
  );
};

export default PayoutTable;
